import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MessageCircle, Plus, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { StudioLayout } from "./StudioLayout";
import { EmptyState, ListSkeleton } from "@/components/studio/StudioUI";
import { MessageCard } from "@/components/studio/ClientQuestions";
import { studio, apiError, inputCls, btnPrimary } from "@/lib/studioApi";

const AddQuestion = ({ onAdded, onClose }) => {
  const [form, setForm] = useState({ doc_type: "devis", doc_ref: "", client_name: "", client_email: "", question: "" });
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const save = async () => {
    setBusy(true);
    try { const { data } = await studio.post("/messages", form); onAdded(data); toast.success("Question enregistrée."); }
    catch (e) { toast.error(apiError(e, "Enregistrement impossible.")); } finally { setBusy(false); }
  };
  return (
    <div data-testid="message-add-form" className="mt-4 rounded-2xl border border-[#C9D4F4] bg-[#F6F8FD] p-4 sm:p-5">
      <div className="flex items-center justify-between"><p className="font-heading text-sm font-bold text-[#0F2645]">Coller une question reçue par e-mail / SMS</p><button type="button" data-testid="message-add-close" onClick={onClose} className="text-[#93A0BC] hover:text-[#0F2645]"><X className="h-4 w-4" /></button></div>
      <div className="mt-3 grid gap-3 sm:grid-cols-2">
        <select data-testid="message-add-type" className={inputCls} value={form.doc_type} onChange={set("doc_type")}><option value="devis">Au sujet d'un devis</option><option value="facture">Au sujet d'une facture</option><option value="libre">Question générale</option></select>
        {form.doc_type !== "libre" && <input data-testid="message-add-ref" className={inputCls} placeholder={form.doc_type === "devis" ? "N° du devis (ex. DEV-2026-0001)" : "N° de facture (ex. FAC-2026-0001)"} value={form.doc_ref} onChange={set("doc_ref")} />}
        <input data-testid="message-add-name" className={inputCls} placeholder="Nom du client (sinon repris du document)" value={form.client_name} onChange={set("client_name")} />
        <input data-testid="message-add-email" type="email" className={inputCls} placeholder="E-mail du client (sinon repris du document)" value={form.client_email} onChange={set("client_email")} />
      </div>
      <textarea data-testid="message-add-question" rows={4} className={`${inputCls} mt-3`} placeholder="Collez ici le message du client…" value={form.question} onChange={set("question")} />
      <button type="button" data-testid="message-add-save" disabled={busy} onClick={save} className={`${btnPrimary} mt-3`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Enregistrer la question</button>
    </div>
  );
};

export default function StudioMessages() {
  const [items, setItems] = useState(null);
  const [tab, setTab] = useState("nouvelle");
  const [params] = useSearchParams();
  const focusId = params.get("repondre") || "";
  const [adding, setAdding] = useState(false);
  useEffect(() => {
    studio.get("/messages").then(({ data }) => {
      setItems(data.items);
      const target = focusId && data.items.find((m) => m.id === focusId);
      if (target) setTab(target.status === "nouvelle" ? "nouvelle" : "repondue");
    }).catch(() => setItems([]));
  }, [focusId]);

  const update = (msg) => setItems((l) => l.map((x) => (x.id === msg.id ? { ...x, ...msg } : x)));
  const remove = async (m) => {
    if (!window.confirm("Supprimer cette question ?")) return;
    try { await studio.delete(`/messages/${m.id}`); setItems((l) => l.filter((x) => x.id !== m.id)); } catch (e) { toast.error(apiError(e)); }
  };
  const open = (items || []).filter((m) => m.status === "nouvelle");
  const done = (items || []).filter((m) => m.status !== "nouvelle");
  const visible = tab === "nouvelle" ? open : done;

  return (
    <StudioLayout title="Questions clients" subtitle="Les questions posées sur vos devis et factures arrivent ici. Claude rédige la réponse à partir du document : relisez, ajustez, envoyez."
      action={<button type="button" data-testid="message-add-btn" onClick={() => setAdding(true)} className={btnPrimary}><Plus className="h-4 w-4" /> Ajouter une question</button>}>
      {adding && <AddQuestion onClose={() => setAdding(false)} onAdded={(m) => { setItems((l) => [m, ...(l || [])]); setAdding(false); setTab("nouvelle"); }} />}
      <div data-testid="messages-tabs" className="mt-6 flex gap-2">
        {[["nouvelle", "À répondre", open.length], ["repondue", "Répondues", done.length]].map(([k, label, n]) => (
          <button key={k} type="button" data-testid={`messages-tab-${k}`} onClick={() => setTab(k)} className={`rounded-lg border px-4 py-1.5 text-sm font-semibold transition-colors ${tab === k ? "border-[#0F2645] bg-[#0F2645] text-white" : "border-[#E2E7F7] bg-white text-[#0F2645]"}`}>{label} <span className="ml-1 font-mono text-xs opacity-70">{n}</span></button>
        ))}
      </div>
      <div data-testid="messages-list" className="mt-4 space-y-3">
        {items === null && <ListSkeleton />}
        {visible.map((m) => <MessageCard key={m.id} m={m} onChange={update} onDelete={remove} autoOpen={m.id === focusId} />)}
        {items !== null && !visible.length && <EmptyState testId="messages-empty" icon={MessageCircle} title={tab === "nouvelle" ? "Aucune question en attente" : "Aucune réponse envoyée"} hint="Vos clients peuvent poser une question depuis la page de leur devis ou facture. Vous pouvez aussi coller un e-mail reçu." />}
      </div>
    </StudioLayout>
  );
}
